import { BSM, Hash, MerklePath, P2PKH, PrivateKey, Script, Transaction, Utils } from '@bsv/sdk'

// Local signing shadow mode: re-sign a server-signed transaction with this device's
// copy of the wallet's keys. Signatures are deterministic (RFC 6979), so a device that
// holds the right key reproduces the server's transaction, txid included. The result is
// only compared; it is never broadcast.

// BRC-95 Atomic BEEF: 4-byte magic + 32-byte subject txid, then a BEEF V1 (BRC-62).
const ATOMIC_BEEF_PREFIX = '01010101'
const ATOMIC_HEADER_BYTES = 36

/**
 * The subject (last) transaction of an (Atomic) BEEF, with the transactions it spends
 * linked as its inputs' sources. Parsed by hand: the server's BEEF carries only the
 * subject's parents and no merkle proofs, which @bsv/sdk's fromBEEF rejects as an
 * incomplete ancestry. Signing needs no more than the parents' outputs.
 */
export function transactionFromBeefHex(beefHex: string): Transaction {
    const bytes = Utils.toArray(beefHex, 'hex')
    const beef = beefHex.toLowerCase().startsWith(ATOMIC_BEEF_PREFIX)
        ? bytes.slice(ATOMIC_HEADER_BYTES)
        : bytes
    const reader = new Utils.Reader(beef)
    reader.readUInt32LE() // BEEF version
    const bumpCount = reader.readVarIntNum()
    for (let i = 0; i < bumpCount; i += 1) MerklePath.fromReader(reader)
    const txCount = reader.readVarIntNum()
    const byTxid = new Map<string, Transaction>()
    let subject: Transaction | null = null
    for (let i = 0; i < txCount; i += 1) {
        const tx = Transaction.fromReader(reader)
        if (reader.readUInt8() === 1) reader.readVarIntNum() // index of this tx's bump
        byTxid.set(tx.id('hex') as string, tx)
        subject = tx
    }
    if (!subject) throw new Error('The BEEF holds no transaction')
    for (const input of subject.inputs) {
        const source = input.sourceTXID ? byTxid.get(input.sourceTXID) : undefined
        if (source) input.sourceTransaction = source
    }
    return subject
}

export interface ShadowSignResult {
    txid: string
    signedInputs: number
    foreignInputs: number
}

const SIGHASH_ANYONECANPAY = 0x80
const SIGHASH_BASE = { 1: 'all', 2: 'none', 3: 'single' } as const

/** The sighash flags of the server's signature on this input (its unlocking script's
 * first push: DER signature + one sighash byte). */
function serverSighash(unlockingHex: string | undefined) {
    if (!unlockingHex) return { signOutputs: 'all' as const, anyoneCanPay: false }
    const bytes = Utils.toArray(unlockingHex, 'hex')
    const pushLength = bytes[0]
    // A P2PKH unlock opens with a direct push (1-75 bytes) of signature + sighash byte.
    if (!pushLength || pushLength > 75) throw new Error('Unexpected unlocking script')
    const sighash = bytes[pushLength]
    const base = SIGHASH_BASE[(sighash & 0x1f) as 1 | 2 | 3]
    if (!base) throw new Error(`Unsupported sighash type ${sighash}`)
    return { signOutputs: base, anyoneCanPay: Boolean(sighash & SIGHASH_ANYONECANPAY) }
}

const OP_DUP = 0x76
const OP_HASH160 = 0xa9
const OP_EQUALVERIFY = 0x88
const OP_CHECKSIG = 0xac

/** The public key hash of the P2PKH a locking script executes, looked for opcode by
 * opcode so bytes pushed as data (an inscription's content) never count. */
function p2pkhHash(lockingScript: Script): string | null {
    const { chunks } = lockingScript
    for (let i = 0; i + 4 < chunks.length; i += 1) {
        if (
            chunks[i].op === OP_DUP &&
            chunks[i + 1].op === OP_HASH160 &&
            chunks[i + 2].data?.length === 20 &&
            chunks[i + 3].op === OP_EQUALVERIFY &&
            chunks[i + 4].op === OP_CHECKSIG
        ) {
            return Utils.toHex(chunks[i + 2].data as number[])
        }
    }
    return null
}

/**
 * Re-sign every input one of the wallet's keys can spend and return the txid. An input
 * is the key's when its locking script runs the key's P2PKH (a 1Sat ordinal output adds
 * an inscription around it). Each input is signed with the server's own sighash flags
 * (mints sign ALL|ANYONECANPAY). Inputs locked to other keys keep the server's unlocking
 * script.
 */
export async function resignOwnInputs(
    tx: Transaction,
    keyWifs: string[],
): Promise<ShadowSignResult> {
    const keys = keyWifs.map(wif => {
        const key = PrivateKey.fromWif(wif)
        return { key, pubKeyHash: Utils.toHex(key.toPublicKey().toHash() as number[]) }
    })
    let signedInputs = 0
    let foreignInputs = 0
    for (const input of tx.inputs) {
        const source = input.sourceTransaction?.outputs[input.sourceOutputIndex]
        if (!source) throw new Error('The transaction is missing an input it spends')
        const lockHash = p2pkhHash(source.lockingScript)
        const owner = keys.find(({ pubKeyHash }) => pubKeyHash === lockHash)
        if (!owner) {
            foreignInputs += 1
            continue
        }
        const { signOutputs, anyoneCanPay } = serverSighash(input.unlockingScript?.toHex())
        input.unlockingScript = undefined
        input.unlockingScriptTemplate = new P2PKH().unlock(owner.key, signOutputs, anyoneCanPay)
        signedInputs += 1
    }
    if (signedInputs === 0) throw new Error('None of the inputs belong to this device key')
    await tx.sign()
    return { txid: tx.id('hex') as string, signedInputs, foreignInputs }
}

// AIP author tuple in a bSocial post's OP_RETURN (bsv/src/bsv/aipSign.ts): '|', the AIP
// prefix, 'BITCOIN_ECDSA', the author's address, then the compact signature.
const AIP_PROTOCOL_ADDRESS = '15PciHG22SNLQJXMoSUaWVi7WSqc7hCfva'
const PIPE = 0x7c

interface PushedField {
    data: number[]
    /** Offset of the data within the script. */
    offset: number
}

/** The data pushes of an `OP_FALSE OP_RETURN` script, or null for any other script. */
function opReturnFields(script: number[]): PushedField[] | null {
    if (script[0] !== 0x00 || script[1] !== 0x6a) return null
    const fields: PushedField[] = []
    let at = 2
    while (at < script.length) {
        const op = script[at]
        let length: number
        let header: number
        if (op >= 0x01 && op <= 0x4b) [length, header] = [op, 1]
        else if (op === 0x4c) [length, header] = [script[at + 1], 2]
        else if (op === 0x4d) [length, header] = [script[at + 1] | (script[at + 2] << 8), 3]
        else if (op === 0x4e)
            [length, header] = [
                (script[at + 1] | (script[at + 2] << 8) | (script[at + 3] << 16)) +
                    script[at + 4] * 0x1000000,
                5,
            ]
        else if (op === 0x00) [length, header] = [0, 1]
        else throw new Error(`Unexpected opcode ${op} in the post data`)
        const offset = at + header
        if (offset + length > script.length) throw new Error('Truncated push in the post data')
        fields.push({ data: script.slice(offset, offset + length), offset })
        at = offset + length
    }
    return fields
}

const isText = (field: PushedField | undefined, text: string) =>
    field !== undefined && Utils.toUTF8(field.data) === text

/**
 * Re-sign a server-signed post's AIP author tuple with this device's payment key and
 * return the txid the post would have with the device's signature in place. Signatures
 * are deterministic, so it equals the server's txid exactly when the device produced the
 * same signature bytes. The tuple must name this key's address.
 */
export function resignPostAip(tx: Transaction, paymentKeyWif: string): ShadowSignResult {
    const key = PrivateKey.fromWif(paymentKeyWif)
    for (const output of tx.outputs) {
        const script = output.lockingScript.toBinary()
        const fields = opReturnFields(script)
        if (!fields) continue
        const tuple = fields.findIndex(
            (field, i) =>
                field.data.length === 1 &&
                field.data[0] === PIPE &&
                isText(fields[i + 1], AIP_PROTOCOL_ADDRESS) &&
                isText(fields[i + 2], 'BITCOIN_ECDSA'),
        )
        if (tuple < 0) continue
        if (!isText(fields[tuple + 3], key.toAddress())) {
            throw new Error("The post's AIP author is not this device key")
        }
        const serverSignature = fields[tuple + 4]
        if (!serverSignature) throw new Error('The AIP tuple has no signature')
        // aipMessageForOps: 0x6a, every field before the tuple's pipe, then the pipe.
        const message = [0x6a, ...fields.slice(0, tuple).flatMap(field => field.data), PIPE]
        const deviceSignature = Utils.toArray(BSM.sign(message, key, 'base64') as string, 'base64')
        if (deviceSignature.length !== serverSignature.data.length) {
            throw new Error('The AIP signature has an unexpected length')
        }
        const rewritten = [...script]
        rewritten.splice(serverSignature.offset, deviceSignature.length, ...deviceSignature)
        output.lockingScript = Script.fromBinary(rewritten)
        return {
            txid: Utils.toHex(Hash.hash256(tx.toBinary()).reverse()),
            signedInputs: 1,
            foreignInputs: 0,
        }
    }
    throw new Error('The transaction carries no AIP-signed post')
}
